// ============================================================================
// INTERACTIVE DRAFT SIMULATOR — 2 ROUNDS (60 PICKS)
// Clean ownership model: each slot is a fixed position, we just check who owns it
// Full TradeMachine popup for mid-draft trades (via parent callback)
// Progress saving for re-entry (navigate away and come back)
// ============================================================================
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { DRAFT_ORDER_2026, getTierLabel, getTierColor, getDraftProspects } from './draftProspects';
import { getRookieScaleSalary } from './seasonAdvance';
import { getPlayerRating, getRatingColor } from './playerRatings';
import PLAYER_POSITIONS from './playerPositions';

const formatSalary = (n) => {
  if (!n || n === 0) return '$0';
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  return '$' + (n / 1000).toFixed(0) + 'K';
};

const TEAMS_MAP = {
  ATL:'Hawks',BOS:'Celtics',BKN:'Nets',CHA:'Hornets',CHI:'Bulls',CLE:'Cavaliers',
  DAL:'Mavericks',DEN:'Nuggets',DET:'Pistons',GSW:'Warriors',HOU:'Rockets',IND:'Pacers',
  LAC:'Clippers',LAL:'Lakers',MEM:'Grizzlies',MIA:'Heat',MIL:'Bucks',MIN:'Timberwolves',
  NOP:'Pelicans',NYK:'Knicks',OKC:'Thunder',ORL:'Magic',PHI:'76ers',PHX:'Suns',
  POR:'Trail Blazers',SAC:'Kings',SAS:'Spurs',TOR:'Raptors',UTA:'Jazz',WAS:'Wizards'
};
const teamName = (a) => TEAMS_MAP[a] || a;
const teamLogo = (a) => {
  const ids = {ATL:1610612737,BOS:1610612738,BKN:1610612751,CHA:1610612766,CHI:1610612741,CLE:1610612739,DAL:1610612742,DEN:1610612743,DET:1610612765,GSW:1610612744,HOU:1610612745,IND:1610612754,LAC:1610612746,LAL:1610612747,MEM:1610612763,MIA:1610612748,MIL:1610612749,MIN:1610612750,NOP:1610612740,NYK:1610612752,OKC:1610612760,ORL:1610612753,PHI:1610612755,PHX:1610612756,POR:1610612757,SAC:1610612758,SAS:1610612759,TOR:1610612761,UTA:1610612762,WAS:1610612764};
  return `https://cdn.nba.com/logos/nba/${ids[a]}/global/L/logo.svg`;
};

const TOTAL_PICKS = 60;
const AIBadge = () => <span style={{ display: 'inline-block', fontSize: 8, fontWeight: 700, color: 'var(--accent-blue, #4a9eff)', background: 'rgba(74,158,255,0.12)', borderRadius: 3, padding: '1px 4px', marginLeft: 5, verticalAlign: 'middle', letterSpacing: 0.5 }}>AI</span>;

// ============================================================================
// AI PICK LOGIC
// ============================================================================
function getTeamNeeds(team, salaryData) {
  const td = salaryData?.[team];
  if (!td?.players) return { PG: 1, SG: 1, SF: 1, PF: 1, C: 1 };
  const posCounts = { PG: 0, SG: 0, SF: 0, PF: 0, C: 0 };
  for (const p of td.players) {
    if (p.isDead) continue;
    const pos = PLAYER_POSITIONS[p.name] || 'SF';
    if (posCounts[pos] !== undefined) posCounts[pos]++;
  }
  const needs = {};
  for (const [pos, count] of Object.entries(posCounts)) {
    needs[pos] = Math.max(0, 3 - count);
  }
  return needs;
}

function aiPickProspect(available, team, salaryData, pickNumber) {
  if (available.length === 0) return null;

  // Pick 1: Peterson hard-lock
  if (pickNumber === 1) {
    const peterson = available.find(p => p.name === 'Darryn Peterson');
    if (peterson) return peterson;
  }

  // Dybantsa top-5 protection
  if (pickNumber <= 5) {
    const dybantsa = available.find(p => p.name === 'AJ Dybantsa');
    if (dybantsa) {
      // If Dybantsa is still available in top 5, strongly prefer him
      const bestAvail = available[0];
      if (dybantsa.rank <= bestAvail.rank + 2) return dybantsa;
    }
  }

  const needs = getTeamNeeds(team, salaryData);
  const scored = available.map(p => {
    const rankScore = 100 - p.rank;
    const positions = p.pos.split('/');
    const needBonus = positions.reduce((max, pos) => {
      const mapped = pos === 'G' ? 'PG' : pos === 'F' ? 'SF' : pos;
      return Math.max(max, (needs[mapped] || 0) * 5);
    }, 0);

    // Controlled variance by pick range:
    // Top 5: no variance (BPA)
    // 6-8: ±1 spot max (small variance)
    // 9-13: ±2 spots max (medium variance)
    // 14-30: ±3-5 spots max (larger variance)
    // 31+: wider variance (second round is unpredictable)
    let varianceRange;
    if (pickNumber <= 5) varianceRange = 0;
    else if (pickNumber <= 8) varianceRange = 3;
    else if (pickNumber <= 13) varianceRange = 6;
    else if (pickNumber <= 30) varianceRange = 12;
    else varianceRange = 16;

    const variance = (Math.random() - 0.5) * varianceRange;
    let score = rankScore + needBonus + variance;
    return { prospect: p, score };
  });
  scored.sort((a, b) => b.score - a.score);
  return scored[0].prospect;
}

// ============================================================================
// BUILD SLOT OWNERSHIP
// slots = fixed array of { slotNum, originalTeam, round } — NEVER changes
// ownership = { slotNum: currentOwnerAbbr } — changes when picks are traded
// ============================================================================
function buildSlotOwnership(standings, tradeHistory, draftYear) {
  let r1Teams;
  if (standings) {
    const allTeams = [...(standings.east || []), ...(standings.west || [])];
    allTeams.sort((a, b) => {
      const pctA = a.wins / Math.max(1, a.wins + a.losses);
      const pctB = b.wins / Math.max(1, b.wins + b.losses);
      return pctA - pctB;
    });
    r1Teams = allTeams.slice(0, 30).map(t => t.abbr);
  } else {
    r1Teams = DRAFT_ORDER_2026.map(s => s.team);
  }

  const slots = [];
  for (let i = 0; i < 30; i++) slots.push({ slotNum: i + 1, originalTeam: r1Teams[i], round: 1 });
  for (let i = 0; i < 30; i++) slots.push({ slotNum: 31 + i, originalTeam: r1Teams[i], round: 2 });

  const ownership = {};
  for (const slot of slots) ownership[slot.slotNum] = slot.originalTeam;

  // Apply pre-draft trades from tradeHistory
  for (const trade of (tradeHistory || [])) {
    if (!trade.picks) continue;
    const teams = trade.teams || [];
    const pDests = trade.pickDestinations || {};
    for (const [sender, sentPicks] of Object.entries(trade.picks)) {
      for (const pick of (sentPicks || [])) {
        if (pick.year !== draftYear || !pick.round) continue;
        let receiver;
        if (teams.length === 2) {
          receiver = teams.find(t => t !== sender) || '';
        } else {
          const senderDests = pDests[sender] || {};
          receiver = senderDests[pick.id] || Object.values(senderDests)[0] || teams.find(t => t !== sender) || '';
        }
        if (!receiver) continue;
        const isR1 = pick.round === 1;
        const slotRange = isR1 ? [1, 30] : [31, 60];
        const targetSlot = slots.find(s =>
          s.slotNum >= slotRange[0] && s.slotNum <= slotRange[1] &&
          ownership[s.slotNum] === sender
        );
        if (targetSlot) ownership[targetSlot.slotNum] = receiver;
      }
    }
  }
  return { slots, ownership };
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const DraftSim = ({
  myTeam, salaryData, standings, draftState, tradeHistory = [],
  onDraftPick, onDraftComplete, onOpenTradeMachine, onProgress, seasonYear
}) => {
  const savedPick = draftState?.currentPick || 0;
  const savedResults = draftState?.results || [];
  const [phase, setPhase] = useState(savedResults.length >= TOTAL_PICKS ? 'recap' : savedPick > 0 ? 'live' : 'start');
  const [currentPick, setCurrentPick] = useState(savedPick);
  const [results, setResults] = useState(savedResults);
  const [showUserPick, setShowUserPick] = useState(false);
  const [showProspectDetail, setShowProspectDetail] = useState(null);
  const [isSimming, setIsSimming] = useState(false);
  const pickRef = useRef(null);
  const activeTeam = myTeam;
  const draftYear = (seasonYear || 2025) + 1;

  // ---- FIXED SLOTS + OWNERSHIP MAP ----
  const { slots } = useMemo(
    () => buildSlotOwnership(standings, tradeHistory, draftYear),
    [standings, tradeHistory, draftYear]
  );

  // Live ownership: initialized from standings+trades, re-syncs when tradeHistory changes
  const [ownership, setOwnership] = useState(() => {
    const base = buildSlotOwnership(standings, tradeHistory, draftYear).ownership;
    const saved = draftState?.pickOwnerOverrides || {};
    return { ...base, ...saved };
  });

  // Re-sync ownership when tradeHistory changes (e.g. user made a trade in the trade machine)
  useEffect(() => {
    const fresh = buildSlotOwnership(standings, tradeHistory, draftYear).ownership;
    setOwnership(fresh);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradeHistory, draftYear]);

  const getOwner = useCallback((slotNum) => ownership[slotNum] || '???', [ownership]);
  const getVia = useCallback((slotNum) => {
    const slot = slots.find(s => s.slotNum === slotNum);
    if (!slot) return null;
    return getOwner(slotNum) !== slot.originalTeam ? slot.originalTeam : null;
  }, [slots, getOwner]);

  const mySlotNumbers = useMemo(
    () => slots.filter(s => getOwner(s.slotNum) === activeTeam).map(s => s.slotNum),
    [slots, getOwner, activeTeam]
  );

  const prospects = useMemo(() => {
    const raw = getDraftProspects(draftYear);
    // Filter out players already on rosters (prevents duplicates across draft years)
    if (salaryData) {
      const rostered = new Set();
      for (const abbr of Object.keys(salaryData)) {
        if (salaryData[abbr]?.players) {
          for (const p of salaryData[abbr].players) {
            if (p.name) rostered.add(p.name);
          }
        }
      }
      return raw.filter(p => !rostered.has(p.name));
    }
    return raw;
  }, [draftYear, salaryData]);

  const draftedNames = useMemo(() => new Set(results.map(r => r.prospect?.name)), [results]);
  const available = useMemo(() => prospects.filter(p => !draftedNames.has(p.name)), [draftedNames, prospects]);

  const currentSlotData = slots[currentPick];
  const currentOwner = currentSlotData ? getOwner(currentSlotData.slotNum) : null;
  const isMyPick = currentOwner === activeTeam;
  const isDone = currentPick >= TOTAL_PICKS;
  const currentRound = currentPick < 30 ? 1 : 2;

  useEffect(() => {
    if (pickRef.current) pickRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [currentPick]);

  // ---- SAVE PROGRESS so user can navigate away and come back ----
  useEffect(() => {
    if (onProgress && phase === 'live') {
      onProgress(currentPick, results);
    }
  }, [currentPick, results, phase, onProgress]);

  // ---- PICK FUNCTIONS ----
  const simToMyPick = useCallback(() => {
    if (isDone) return;
    setIsSimming(true);
    let pick = currentPick;
    let res = [...results];
    const ownSnap = { ...ownership };

    const simNext = () => {
      if (pick >= TOTAL_PICKS) {
        setIsSimming(false);
        setCurrentPick(pick);
        setResults(res);
        if (res.length >= TOTAL_PICKS) setPhase('recap');
        return;
      }
      const slot = slots[pick];
      const owner = ownSnap[slot.slotNum] || slot.originalTeam;
      if (owner === activeTeam) {
        setCurrentPick(pick);
        setResults(res);
        setShowUserPick(true);
        setIsSimming(false);
        return;
      }
      const drafted = new Set(res.map(r => r.prospect?.name));
      const stillAvail = prospects.filter(p => !drafted.has(p.name));
      const prospect = aiPickProspect(stillAvail, owner, salaryData, slot.slotNum);
      if (prospect) {
        const via = owner !== slot.originalTeam ? slot.originalTeam : null;
        const result = { pick: slot.slotNum, team: owner, via, prospect, salary: getRookieScaleSalary(slot.slotNum), round: slot.round };
        res.push(result);
        onDraftPick(result);
      }
      pick++;
      setTimeout(simNext, pick <= 30 ? 120 : 60);
    };
    simNext();
  }, [currentPick, isDone, results, slots, ownership, activeTeam, salaryData, onDraftPick, prospects]);

  const simNextPick = useCallback(() => {
    if (isDone || isMyPick) return;
    const slot = slots[currentPick];
    const owner = getOwner(slot.slotNum);
    const prospect = aiPickProspect(available, owner, salaryData, slot.slotNum);
    if (prospect) {
      const via = getVia(slot.slotNum);
      const result = { pick: slot.slotNum, team: owner, via, prospect, salary: getRookieScaleSalary(slot.slotNum), round: slot.round };
      setResults(prev => [...prev, result]);
      setCurrentPick(prev => prev + 1);
      onDraftPick(result);
    }
  }, [isDone, isMyPick, slots, currentPick, available, salaryData, onDraftPick, getOwner, getVia]);

  const simMyOwnPick = useCallback(() => {
    if (!isMyPick || isDone) return;
    const slot = slots[currentPick];
    const prospect = aiPickProspect(available, activeTeam, salaryData, slot.slotNum);
    if (prospect) {
      const via = getVia(slot.slotNum);
      const result = { pick: slot.slotNum, team: activeTeam, via, prospect, salary: getRookieScaleSalary(slot.slotNum), round: slot.round };
      setResults(prev => [...prev, result]);
      setCurrentPick(prev => prev + 1);
      setShowUserPick(false);
      onDraftPick(result);
    }
  }, [isMyPick, isDone, slots, currentPick, available, activeTeam, salaryData, onDraftPick, getVia]);

  const handleUserSelect = useCallback((prospect) => {
    const slot = slots[currentPick];
    const via = getVia(slot.slotNum);
    const result = { pick: slot.slotNum, team: activeTeam, via, prospect, salary: getRookieScaleSalary(slot.slotNum), isUserPick: true, round: slot.round };
    setResults(prev => [...prev, result]);
    setCurrentPick(prev => prev + 1);
    setShowUserPick(false);
    setShowProspectDetail(null);
    onDraftPick(result);
  }, [currentPick, slots, activeTeam, onDraftPick, getVia]);

  // ---- OPEN FULL TRADE MACHINE (via parent) ----
  const handleOpenTrade = useCallback(() => {
    setShowUserPick(false);
    if (onOpenTradeMachine) onOpenTradeMachine();
  }, [onOpenTradeMachine]);

  // After user returns from trade machine, check if it's still our pick
  useEffect(() => {
    if (phase === 'live' && !isSimming && !showUserPick && !isDone) {
      const slot = slots[currentPick];
      if (slot && getOwner(slot.slotNum) === activeTeam) {
        setShowUserPick(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownership]);

  useEffect(() => {
    if (currentPick >= TOTAL_PICKS && !isSimming && results.length >= TOTAL_PICKS && phase === 'live') {
      setPhase('recap');
    }
  }, [currentPick, isSimming, results, phase]);

  // ========================================================================
  // START SCREEN
  // ========================================================================
  if (phase === 'start') {
    const r1Picks = mySlotNumbers.filter(p => p <= 30);
    const r2Picks = mySlotNumbers.filter(p => p > 30);
    return (
      <div className="draft-sim fade-in">
        <div className="draft-start-screen">
          <div className="draft-start-label">{draftYear} NBA Draft</div>
          <div className="draft-start-title">Rounds 1 &amp; 2</div>
          <div className="draft-start-sub">
            60 picks across 2 rounds.
            {r1Picks.length > 0 && ` R1: #${r1Picks.join(', #')}.`}
            {r2Picks.length > 0 && ` R2: #${r2Picks.join(', #')}.`}
            {mySlotNumbers.length === 0 && ' You have no draft picks.'}
          </div>
          <div className="draft-start-preview">
            {slots.slice(0, 10).map((slot, i) => {
              const owner = getOwner(slot.slotNum);
              const via = getVia(slot.slotNum);
              return (
                <div key={i} className={`draft-start-preview-row ${owner === activeTeam ? 'my-team' : ''}`}>
                  <span className="draft-start-preview-num">{slot.slotNum}</span>
                  <img src={teamLogo(owner)} alt="" style={{ width: 18, height: 18 }} />
                  <span className="draft-start-preview-name">{teamName(owner)}</span>
                  {via && <span style={{ fontSize: 9, color: 'var(--text-dim)' }}>via {via}</span>}
                </div>
              );
            })}
            <div className="draft-start-preview-more">+ 50 more picks</div>
          </div>
          <div className="draft-start-actions">
            <button className="draft-start-btn primary" onClick={() => { setPhase('live'); simToMyPick(); }}>Sim to My Pick</button>
            <button className="draft-start-btn secondary" onClick={() => setPhase('live')}>Pick by Pick</button>
          </div>
        </div>
      </div>
    );
  }

  // ========================================================================
  // DRAFT RECAP
  // ========================================================================
  if (phase === 'recap') {
    const myPicks = results.filter(r => r.team === activeTeam);
    return (
      <div className="draft-sim fade-in">
        <div className="draft-sim-header">
          <div>
            <div className="fa-board-label">Draft Complete</div>
            <div className="fa-board-title">{draftYear} NBA Draft Results</div>
          </div>
        </div>
        {myPicks.length > 0 && (
          <div className="draft-recap-my-picks">
            <div className="fa-detail-title">Your Selections</div>
            {myPicks.map(r => {
              const tierColor = getTierColor(r.prospect.tier);
              return (
                <div key={r.pick} className="draft-sim-row past my-team" style={{ borderLeftColor: tierColor }}>
                  <div className="draft-sim-pick-num">{r.pick}</div>
                  <div className="draft-sim-result">
                    <div className="draft-sim-prospect-name">{r.prospect.name}{r.prospect.aiGenerated && <AIBadge />}</div>
                    <div className="draft-sim-prospect-meta">{r.prospect.pos} &middot; {r.prospect.school} &middot; {formatSalary(r.salary)}/yr</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {[1, 2].map(round => (
          <div key={round} style={{ marginTop: 20 }}>
            <div className="fa-detail-title">Round {round}</div>
            {results.filter(r => r.round === round).map(r => (
              <div key={r.pick} className={`draft-sim-row past ${r.team === activeTeam ? 'my-team' : ''}`}>
                <div className="draft-sim-pick-num">{r.pick}</div>
                <div className="draft-sim-team">
                  <img src={teamLogo(r.team)} alt="" className="draft-sim-logo" />
                  <span className="draft-sim-team-name">{teamName(r.team)}</span>
                  {r.via && <span className="draft-sim-via">via {r.via}</span>}
                </div>
                <div className="draft-sim-result">
                  <div className="draft-sim-prospect-name">{r.prospect.name}{r.prospect.aiGenerated && <AIBadge />}</div>
                  <div className="draft-sim-prospect-meta">{r.prospect.pos} &middot; {r.prospect.school}</div>
                </div>
              </div>
            ))}
          </div>
        ))}
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <button className="draft-start-btn primary" onClick={() => onDraftComplete(results)}>Continue to Offseason &rarr;</button>
        </div>
      </div>
    );
  }

  // ========================================================================
  // LIVE DRAFT
  // ========================================================================
  return (
    <div className="draft-sim fade-in">
      <div className="draft-sim-header">
        <div>
          <div className="fa-board-label">{draftYear} NBA Draft</div>
          <div className="fa-board-title">Round {currentRound} &mdash; Pick {Math.min(currentPick + 1, TOTAL_PICKS)}</div>
        </div>
        <div className="fa-board-actions">
          {isSimming && <div className="draft-simming-indicator">Simulating picks...</div>}
          {!isSimming && !showUserPick && !isDone && !isMyPick && (
            <>
              <button className="fa-back-btn" onClick={simNextPick}>Next Pick</button>
              <button className="fa-skip-btn" onClick={simToMyPick}>Sim to My Pick</button>
            </>
          )}
          {!isSimming && isMyPick && !showUserPick && (
            <button className="recap-fa-btn" onClick={() => setShowUserPick(true)}>Your Pick &mdash; Select Prospect</button>
          )}
          {/* Always show trade button during live draft */}
          {!isSimming && !isDone && onOpenTradeMachine && (
            <button className="fa-back-btn" onClick={handleOpenTrade} style={{ marginLeft: 4 }}>Trade</button>
          )}
        </div>
      </div>

      <div className="draft-sim-board">
        {slots.map((slot, i) => {
          const result = results[i];
          const isCurrent = i === currentPick && !isDone;
          const isPast = i < currentPick;
          const isFuture = i > currentPick;
          const owner = getOwner(slot.slotNum);
          const via = getVia(slot.slotNum);
          const isUserTeam = owner === activeTeam;
          const showRoundHeader = i === 0 || i === 30;
          return (
            <React.Fragment key={i}>
              {showRoundHeader && <div className="draft-round-header">Round {slot.round}</div>}
              <div
                ref={isCurrent ? pickRef : null}
                className={`draft-sim-row ${isCurrent ? 'current' : ''} ${isPast ? 'past' : ''} ${isFuture ? 'future' : ''} ${isUserTeam ? 'my-team' : ''}`}
              >
                <div className="draft-sim-pick-num">{slot.slotNum}</div>
                <div className="draft-sim-team">
                  <img src={teamLogo(owner)} alt="" className="draft-sim-logo" />
                  <div>
                    <div className={`draft-sim-team-name ${isUserTeam ? 'highlight' : ''}`}>{teamName(owner)}</div>
                    {via && <div className="draft-sim-via">via {via}</div>}
                  </div>
                </div>
                {result ? (
                  <div className="draft-sim-result" onClick={() => setShowProspectDetail(result.prospect)}>
                    <div className="draft-sim-prospect-name">{result.prospect.name}{result.prospect.aiGenerated && <AIBadge />}</div>
                    <div className="draft-sim-prospect-meta">{result.prospect.pos} &middot; {result.prospect.school} &middot; {formatSalary(result.salary)}/yr</div>
                  </div>
                ) : isCurrent ? (
                  <div className="draft-sim-on-clock">
                    <span className="draft-sim-clock-text">{isUserTeam ? 'Your Pick' : 'On the Clock'}</span>
                  </div>
                ) : (
                  <div className="draft-sim-pending">&mdash;</div>
                )}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {showUserPick && isMyPick && (
        <UserPickModal
          pick={currentSlotData.slotNum}
          team={activeTeam}
          available={available}
          onSelect={handleUserSelect}
          onSimMyPick={simMyOwnPick}
          onTrade={handleOpenTrade}
          onViewProspect={setShowProspectDetail}
        />
      )}

      {showProspectDetail && (
        <div className="modal-overlay" onClick={() => setShowProspectDetail(null)}>
          <div className="prospect-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowProspectDetail(null)}>&times;</button>
            <ProspectDetail prospect={showProspectDetail} />
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// USER PICK MODAL
// ============================================================================
const UserPickModal = ({ pick, team, available, onSelect, onSimMyPick, onTrade, onViewProspect }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [posFilter, setPosFilter] = useState('ALL');
  const filtered = available.filter(p => {
    if (searchQuery && !p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (posFilter !== 'ALL' && !p.pos.includes(posFilter)) return false;
    return true;
  }).slice(0, 25);
  const rookieSalary = getRookieScaleSalary(pick);

  return (
    <div className="draft-user-pick-overlay">
      <div className="draft-user-pick">
        <div className="draft-user-pick-header">
          <div>
            <div className="fa-board-label">Pick #{pick} &middot; Round {pick <= 30 ? 1 : 2}</div>
            <div className="fa-board-title" style={{ fontSize: 22 }}>You're on the Clock</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-dim)', marginTop: 4 }}>
              Rookie scale: {formatSalary(rookieSalary)}/yr
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="fa-back-btn" onClick={onSimMyPick}>Sim My Pick</button>
            <button className="fa-skip-btn" onClick={onTrade}>Open Trade Machine</button>
          </div>
        </div>
        <div className="fa-filters" style={{ marginTop: 12 }}>
          <input className="draft-search" placeholder="Search prospects..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus style={{ flex: 1, maxWidth: 250 }} />
          <div className="fa-filter-group">
            {['ALL', 'PG', 'SG', 'SF', 'PF', 'C'].map(pos => (
              <button key={pos} className={`fa-filter-btn ${posFilter === pos ? 'active' : ''}`} onClick={() => setPosFilter(pos)}>{pos}</button>
            ))}
          </div>
        </div>
        <div className="draft-user-pick-list">
          {filtered.map(p => {
            const tierColor = getTierColor(p.tier);
            return (
              <div key={p.rank} className="draft-user-pick-row">
                <div className="draft-user-pick-info" onClick={() => onViewProspect(p)}>
                  <div className="draft-prospect-rank" style={{ color: tierColor }}>{p.rank}</div>
                  <div>
                    <div className="draft-prospect-name">{p.name}{p.aiGenerated && <AIBadge />}</div>
                    <div className="draft-prospect-meta">{p.pos} &middot; {p.school} &middot; {p.height}</div>
                  </div>
                </div>
                <div className="draft-user-pick-stats">
                  <span>{p.pts} PPG</span>
                  <span>{p.reb} RPG</span>
                  <span>{p.ast} APG</span>
                </div>
                <button className="draft-select-btn" onClick={() => onSelect(p)}>Draft</button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PROSPECT DETAIL
// ============================================================================
const ProspectDetail = ({ prospect }) => {
  const tierColor = getTierColor(prospect.tier);
  const ovrColor = getRatingColor(prospect.ovr || 70);
  return (
    <>
      <div className="prospect-modal-header">
        <div className="prospect-modal-label">Prospect #{prospect.rank}</div>
        <div className="prospect-modal-name">{prospect.name}{prospect.aiGenerated && <AIBadge />}</div>
        <div className="prospect-modal-meta">{prospect.pos} &middot; {prospect.school} &middot; {prospect.cls}</div>
      </div>
      <div className="prospect-modal-tier" style={{ borderLeftColor: tierColor }}>
        <span className="prospect-tier-rank" style={{ color: tierColor }}>#{prospect.rank}</span>
        <span className="prospect-tier-label">{getTierLabel(prospect.tier)}</span>
        {prospect.ovr && <span className="ovr-badge" style={{ borderLeftColor: ovrColor, marginLeft: 'auto' }}>{prospect.ovr} OVR</span>}
      </div>
      <div className="prospect-modal-section-title">Measurables</div>
      <div className="prospect-modal-grid">
        <div className="prospect-modal-stat-box"><div className="prospect-stat-box-label">Height</div><div className="prospect-stat-box-val">{prospect.height}</div></div>
        <div className="prospect-modal-stat-box"><div className="prospect-stat-box-label">Weight</div><div className="prospect-stat-box-val">{prospect.weight} lbs</div></div>
        <div className="prospect-modal-stat-box"><div className="prospect-stat-box-label">Age</div><div className="prospect-stat-box-val">{prospect.age.toFixed(1)}</div></div>
      </div>
      <div className="prospect-modal-section-title">Stats</div>
      <div className="prospect-modal-grid">
        <div className="prospect-modal-stat-box"><div className="prospect-stat-box-label">PPG</div><div className="prospect-stat-box-val big">{prospect.pts}</div></div>
        <div className="prospect-modal-stat-box"><div className="prospect-stat-box-label">RPG</div><div className="prospect-stat-box-val big">{prospect.reb}</div></div>
        <div className="prospect-modal-stat-box"><div className="prospect-stat-box-label">APG</div><div className="prospect-stat-box-val big">{prospect.ast}</div></div>
      </div>
      {prospect.scouting && (
        <><div className="prospect-modal-section-title">Scouting Report</div><div className="prospect-modal-scouting" style={{ borderLeftColor: tierColor }}>{prospect.scouting}</div></>
      )}
    </>
  );
};

export default DraftSim;
